import { FilterType } from '@monetize/types/common';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { useGetOpportunities } from '../../api/cpqService';
import {
  MFlex,
  MHStack,
  MPageContentContainer,
  MPageHeader,
} from '../../components/Monetize';
import { useTableColumns } from '../../components/Monetize/DataTableNew/columnCatalog/useTableColumns';
import { DataTable } from '../../components/Monetize/DataTableNew/DataTable';
import { DataTableRef } from '../../components/Monetize/DataTableNew/DataTableTypes';
import { ExportEntityButton } from '../../components/Monetize/ExportEntityButton';
import { RefreshTableButton } from '../../components/Monetize/RefreshTableButton';
import { getSalesOpportunityEditRoute } from '../../constants/routes';
import { useDocumentHead } from '../../services/documentHead';
import {
  tableFilterSelector,
  tablePagerSelector,
} from '../../store/global.store';
import {
  FilterStateKeys,
  getDefaultFilter,
  getDefaultPager,
} from '../../store/store.types';
import {
  buildFilterParamsRequestObject,
  getIsTrulyEmptyList,
} from '../../utils';
import { navigateOrOpenNewWindow } from '../../utils/misc';
import {
  OPPORTUNITY_LIST_CATALOG,
  OpportunityListColumnContext,
} from './opportunityList.columns';

type FilterKey =
  | FilterStateKeys.OPPORTUNITY_LIST
  | FilterStateKeys.OPPORTUNITY_DASHBOARD_LIST;

export const useOpportunitiesTableData = (
  filterKey: FilterKey = FilterStateKeys.OPPORTUNITY_LIST,
) => {
  const [initialFilters, persistTableFilter] = useAtom(
    tableFilterSelector(filterKey),
  );

  const [pager, setPager] = useAtom(tablePagerSelector(filterKey));
  const [filters, setFilters] = useState<FilterType[]>(initialFilters);
  const [searchKey] = useState<string>('description');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const filterParams = useMemo(
    () => buildFilterParamsRequestObject(filters, searchTerm, searchKey),
    [filters, searchTerm, searchKey],
  );
  const { setDocTitle } = useDocumentHead();
  useEffect(() => {
    setDocTitle('Opportunities');
  }, []);

  useEffect(() => {
    persistTableFilter(filters);
  }, [filters, persistTableFilter]);

  const {
    isLoading,
    isRefetching,
    isFetched,
    data: opportunityList,
    isError,
    error,
    refetch,
  } = useGetOpportunities({
    config: pager,
    filters: filterParams,
  });

  const onResetFilter = () => {
    setFilters(getDefaultFilter(filterKey));
    setPager(getDefaultPager(filterKey));
  };

  return {
    opportunityList,
    loading: isLoading || (isRefetching && !isFetched),
    searchTerm,
    setSearchTerm,
    pager,
    setPager,
    filters,
    setFilters,
    onResetFilter,
    isError,
    error,
    refetch,
  };
};

const OpportunityListMain = ({
  renderTableHeader,
}: {
  renderTableHeader?: () => any;
}) => {
  const navigate = useNavigate();
  const tableRef = useRef<DataTableRef>(null);

  const {
    opportunityList,
    loading,
    searchTerm,
    pager,
    setPager,
    filters,
    setFilters,
    onResetFilter,
    refetch,
  } = useOpportunitiesTableData();

  const isTrulyEmptyList = getIsTrulyEmptyList({
    loading,
    totalElements: opportunityList?.totalElements ?? 0,
    filters,
    searchTerm,
    page: pager.page,
  });

  const columnContext = useMemo<OpportunityListColumnContext>(() => ({}), []);
  const { columns, state: columnState } = useTableColumns(
    OPPORTUNITY_LIST_CATALOG,
    columnContext,
  );

  return (
    <MPageContentContainer>
      {renderTableHeader ? (
        <MPageHeader title="Opportunities">{renderTableHeader()}</MPageHeader>
      ) : (
        <MPageHeader size="md" title="Opportunities">
          {!isTrulyEmptyList && (
            <MFlex>
              <MHStack gap="2">
                <RefreshTableButton refetch={refetch} />
                <ExportEntityButton
                  entity="opportunities"
                  filters={filters}
                  sortField={pager.sortField}
                  sortOrder={pager.sortOrder}
                />
              </MHStack>
            </MFlex>
          )}
        </MPageHeader>
      )}
      <DataTable
        ref={tableRef}
        columns={columns}
        state={columnState}
        data={opportunityList?.content ?? []}
        enablePagination
        totalElements={opportunityList?.totalElements}
        totalPages={opportunityList?.totalPages}
        pager={pager}
        setPager={setPager}
        initialFilters={filters}
        onFiltersChange={setFilters}
        onRowClick={(e, _tableRow, openInNewWindow) =>
          navigateOrOpenNewWindow(
            navigate,
            getSalesOpportunityEditRoute(e.id),
            openInNewWindow,
          )
        }
        isLoading={loading}
        emptyProps={{
          mainMessage: 'Looks like there are no opportunities here.',
        }}
        emptyFilterResultsProps={{
          btnLabel: 'Reset Filters',
        }}
        resetFilter={() => {
          onResetFilter();
          tableRef.current?.resetFilters();
        }}
      />
      <Outlet />
    </MPageContentContainer>
  );
};

export default OpportunityListMain;
